import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Instagram, Mail, Menu, X, ArrowRight } from "lucide-react";

const links = [
  { label: "Work", href: "#portfolio", no: "01" },
  { label: "Pillars", href: "#pillars", no: "02" },
  { label: "Numbers", href: "#metrics", no: "03" },
  { label: "Audience", href: "#audience", no: "04" },
  { label: "Collab", href: "#contact", no: "05" },
];

const panelVariants = {
  hidden: { x: "-100%" },
  show: { x: "0%", transition: { type: "spring", stiffness: 260, damping: 30 } },
  exit: { x: "-100%", transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const listContainer = {
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.12 } },
};
const listItem = {
  hidden: { x: -40, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    if (open) {
      setHidden(false);
      return;
    }
    if (latest > prev && latest > 140) {
      setHidden(true); // scrolling down
    } else if (latest < prev) {
      setHidden(false); // scrolling up
    }
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <motion.nav
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: hidden ? -120 : 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        data-testid="main-navbar"
        className="fixed top-4 inset-x-0 z-50 mx-auto w-[95%] max-w-6xl"
      >
        <div className="flex items-center justify-between rounded-full border-[3px] border-clay-ink bg-white/85 px-4 py-2.5 shadow-brutal-sm backdrop-blur-md sm:px-6">
          <a
            href="#top"
            data-testid="nav-logo"
            className="font-display text-lg font-black tracking-tight text-clay-ink"
          >
            HSR<span className="text-clay-violet">.</span>
          </a>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-clay-ink bg-clay-violet text-white shadow-clay-btn transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            <Menu className="h-5 w-5" strokeWidth={2.75} />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              data-testid="nav-scrim"
              className="fixed inset-0 z-[60] bg-clay-ink/40 backdrop-blur-sm"
            />

            <motion.aside
              key="panel"
              variants={panelVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              data-testid="nav-panel"
              className="fixed left-0 top-0 z-[70] flex h-full w-[86%] max-w-sm flex-col justify-between border-r-[3px] border-clay-ink bg-clay-bg p-7 shadow-brutal-lg"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl font-black tracking-tight text-clay-ink">
                    HSR<span className="text-clay-violet">.</span>
                  </span>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="nav-menu-close"
                    aria-label="Close menu"
                    className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-clay-ink bg-white text-clay-ink shadow-brutal-sm transition-transform hover:translate-y-[2px]"
                  >
                    <X className="h-5 w-5" strokeWidth={2.75} />
                  </button>
                </div>

                <motion.nav
                  variants={listContainer}
                  initial="hidden"
                  animate="show"
                  className="mt-10 flex flex-col gap-1"
                >
                  {links.map((l) => (
                    <motion.a
                      key={l.label}
                      href={l.href}
                      variants={listItem}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-link-${l.label.toLowerCase()}`}
                      className="group flex items-baseline gap-4 border-b-2 border-clay-ink/10 py-4 transition-colors hover:border-clay-violet"
                    >
                      <span className="font-body text-xs font-bold text-clay-violet">
                        {l.no}
                      </span>
                      <span className="font-display text-4xl font-black tracking-tighter text-clay-ink transition-transform group-hover:translate-x-2">
                        {l.label}
                      </span>
                      <ArrowRight className="ml-auto h-6 w-6 self-center text-clay-ink/0 transition-all group-hover:text-clay-violet" strokeWidth={2.5} />
                    </motion.a>
                  ))}
                </motion.nav>
              </div>

              <div className="space-y-3">
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  data-testid="nav-collab-btn"
                  className="flex items-center justify-center gap-2 rounded-full border-[3px] border-clay-ink bg-clay-violet px-5 py-3 font-display text-base font-bold text-white shadow-clay-btn transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
                >
                  <Mail className="h-4 w-4" strokeWidth={2.5} />
                  Let&apos;s collaborate
                </a>
                <a
                  href="https://instagram.com/hi._.manganese"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-instagram"
                  className="flex items-center justify-center gap-2 rounded-full border-[3px] border-clay-ink bg-clay-lime px-5 py-3 font-display text-base font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
                >
                  <Instagram className="h-4 w-4" strokeWidth={2.5} />
                  @hi._.manganese
                </a>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
